import { cloudTalkFetchJson, getCloudTalkCredentials } from './cloudTalkApi';
import { formatSupabaseError } from './formatSupabaseError';
import type { PortalAccessMap } from './portalAccess';
import { getDbSupabase, supabase } from './supabase';
import type { DemCloudTalkContact } from '../types/dem';

const CONTACT_SELECT =
  'contact_id,name,company,title,website,industry,address,city,zip,state,country_id,phones,emails,tags,external_crm_contact_id,updated_at';

/** Same gate as recordings: Management / IT only. */
export function canManageCloudTalkContacts(portalAccess: PortalAccessMap | null): boolean {
  const dept = portalAccess?.elevatedDepartment;
  return dept === 'management' || dept === 'it';
}

export type CloudTalkContactInput = {
  name: string;
  company?: string;
  title?: string;
  website?: string;
  industry?: string;
  address?: string;
  city?: string;
  zip?: string;
  state?: string;
  countryId?: number | null;
  phones?: string[];
  emails?: string[];
  tags?: string[];
};

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function rowToContact(row: Record<string, unknown>): DemCloudTalkContact {
  const countryRaw = row.country_id;
  const countryId =
    typeof countryRaw === 'number' && Number.isFinite(countryRaw)
      ? Math.trunc(countryRaw)
      : countryRaw != null && String(countryRaw).trim() !== ''
        ? Number(countryRaw)
        : null;
  return {
    contactId: String(row.contact_id ?? ''),
    name: String(row.name ?? ''),
    company: String(row.company ?? ''),
    title: String(row.title ?? ''),
    website: String(row.website ?? ''),
    industry: String(row.industry ?? ''),
    address: String(row.address ?? ''),
    city: String(row.city ?? ''),
    zip: String(row.zip ?? ''),
    state: String(row.state ?? ''),
    countryId: Number.isFinite(countryId as number) ? (countryId as number) : null,
    phones: asStringList(row.phones),
    emails: asStringList(row.emails),
    tags: asStringList(row.tags),
    externalCrmContactId: String(row.external_crm_contact_id ?? ''),
    updatedAt: String(row.updated_at ?? ''),
  };
}

function cleanList(values: string[] | undefined): string[] {
  return [...new Set((values ?? []).map((v) => v.trim()).filter(Boolean))];
}

function toCloudTalkBody(input: CloudTalkContactInput): Record<string, unknown> {
  const phones = cleanList(input.phones);
  const emails = cleanList(input.emails);
  const tags = cleanList(input.tags);
  const body: Record<string, unknown> = {
    name: input.name.trim(),
    company: (input.company ?? '').trim(),
    title: (input.title ?? '').trim(),
    website: (input.website ?? '').trim(),
    industry: (input.industry ?? '').trim(),
    address: (input.address ?? '').trim(),
    city: (input.city ?? '').trim(),
    zip: (input.zip ?? '').trim(),
    state: (input.state ?? '').trim(),
  };
  if (input.countryId != null && Number.isFinite(input.countryId)) {
    body.country_id = Math.trunc(input.countryId);
  }
  body.ContactNumber = phones.map((public_number) => ({ public_number }));
  body.ContactEmail = emails.map((email) => ({ email }));
  body.ContactsTag = tags.map((name) => ({ name }));
  return body;
}

async function upsertLocalContact(
  contactId: string,
  input: CloudTalkContactInput,
  previous?: DemCloudTalkContact | null,
): Promise<DemCloudTalkContact> {
  const db = getDbSupabase('dem');
  const next = {
    contact_id: contactId,
    name: input.name.trim(),
    company: (input.company ?? '').trim(),
    title: (input.title ?? '').trim(),
    website: (input.website ?? '').trim(),
    industry: (input.industry ?? '').trim(),
    address: (input.address ?? '').trim(),
    city: (input.city ?? '').trim(),
    zip: (input.zip ?? '').trim(),
    state: (input.state ?? '').trim(),
    country_id:
      input.countryId != null && Number.isFinite(input.countryId)
        ? Math.trunc(input.countryId)
        : null,
    phones: cleanList(input.phones),
    emails: cleanList(input.emails),
    tags: cleanList(input.tags),
    external_crm_contact_id: previous?.externalCrmContactId ?? '',
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await db
    .from('cloudtalk_contacts')
    .upsert(next, { onConflict: 'contact_id' })
    .select(CONTACT_SELECT)
    .single();
  if (error) throw new Error(formatSupabaseError(error));
  return rowToContact(data as Record<string, unknown>);
}

export async function fetchCloudTalkContacts(limit = 500): Promise<DemCloudTalkContact[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_contacts')
    .select(CONTACT_SELECT)
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? [])
    .map((row) => rowToContact(row as Record<string, unknown>))
    .filter((row) => row.contactId);
}

export async function syncCloudTalkContactsDirectory(): Promise<number> {
  const { data, error } = await supabase.functions.invoke('cloudtalk-webhook', {
    body: { sync: 'contacts' },
  });
  if (error) throw new Error(error.message || 'CloudTalk contacts sync failed.');
  const result = (data ?? {}) as { ok?: boolean; synced?: number; error?: string };
  if (result.error || result.ok === false) {
    throw new Error(result.error || 'CloudTalk contacts sync failed.');
  }
  return Number(result.synced ?? 0);
}

function extractCreatedId(payload: unknown): string {
  const root = (payload ?? {}) as Record<string, unknown>;
  const responseData = (root.responseData ?? root) as Record<string, unknown>;
  const data = (responseData.data ?? responseData) as Record<string, unknown>;
  const id = String(data.id ?? responseData.id ?? '').trim();
  return id;
}

export async function createCloudTalkContact(
  input: CloudTalkContactInput,
): Promise<DemCloudTalkContact> {
  const name = input.name.trim();
  if (!name) throw new Error('Name is required.');
  const creds = getCloudTalkCredentials();
  if (!creds) {
    throw new Error('CloudTalk API is not configured (missing VITE_CT_API_ID / VITE_CT_API_SECRET).');
  }

  const payload = await cloudTalkFetchJson(
    creds,
    '/contacts/add.json',
    'CloudTalk add contact',
    60_000,
    { method: 'PUT', body: toCloudTalkBody(input) },
  );
  const contactId = extractCreatedId(payload);
  if (!contactId) throw new Error('CloudTalk did not return a contact id.');
  return upsertLocalContact(contactId, input);
}

export async function updateCloudTalkContact(
  contactId: string,
  input: CloudTalkContactInput,
  previous?: DemCloudTalkContact | null,
): Promise<DemCloudTalkContact> {
  const id = String(contactId || '').trim();
  if (!id) throw new Error('contactId is required.');
  const name = input.name.trim();
  if (!name) throw new Error('Name is required.');
  const creds = getCloudTalkCredentials();
  if (!creds) {
    throw new Error('CloudTalk API is not configured (missing VITE_CT_API_ID / VITE_CT_API_SECRET).');
  }

  await cloudTalkFetchJson(
    creds,
    `/contacts/edit/${encodeURIComponent(id)}.json`,
    'CloudTalk edit contact',
    60_000,
    { method: 'POST', body: toCloudTalkBody(input) },
  );
  return upsertLocalContact(id, input, previous);
}

export async function deleteCloudTalkContact(contactId: string): Promise<void> {
  const id = String(contactId || '').trim();
  if (!id) throw new Error('contactId is required.');
  const creds = getCloudTalkCredentials();
  if (!creds) {
    throw new Error('CloudTalk API is not configured (missing VITE_CT_API_ID / VITE_CT_API_SECRET).');
  }

  await cloudTalkFetchJson(
    creds,
    `/contacts/delete/${encodeURIComponent(id)}.json`,
    'CloudTalk delete contact',
    60_000,
    { method: 'DELETE' },
  );

  const db = getDbSupabase('dem');
  const { error } = await db.from('cloudtalk_contacts').delete().eq('contact_id', id);
  if (error) throw new Error(formatSupabaseError(error));
}
